// src/components/SuraQuiz.jsx

import React, { useState, useEffect } from 'react';
import { HiFire, HiShieldCheck,  HiSparkles } from 'react-icons/hi';

// Medal Thresholds
const MEDAL_THRESHOLDS = {
  GOLD: 1500,
  SILVER: 1000,
  BRONZE: 500,
};

// Simplified Quiz Data (In a real app, this should come from a database)
const QUIZ_QUESTIONS = [
  {
    id: 1,
    question:
      'How many obligatory daily prayers (Fard Salah) are there in Islam?',
    answer: 'Five',
    options: ['Five', 'Three', 'Seven'],
  },
  {
    id: 2,
    question: 'Which of the following is the first pillar of Islam?',
    answer: 'Shahada (Declaration of Faith)',
    options: [
      'Shahada (Declaration of Faith)',
      'Salat (Prayer)',
      'Zakat (Charity)',
    ],
  },
  {
    id: 3,
    question: "What is the name of Prophet Muhammad's (PBUH) mother?",
    answer: 'Aminah',
    options: ['Aminah', 'Khadijah', 'Fatimah'],
  },
  {
    id: 4,
    question: "What is the name of Prophet Muhammad's (PBUH) father?",
    answer: 'Abdullah',
    options: ['Abdullah', 'Abu Talib', 'Hamza'],
  },
  {
    id: 5,
    question: 'During which month do Muslims fast?',
    answer: 'Ramadan',
    options: ['Ramadan', 'Shawwal', "Rabi' al-Awwal"],
  },
  {
    id: 6,
    question: 'What is the Arabic term for ritual ablution before prayer?',
    answer: 'Wudu',
    options: ['Wudu', 'Tayammum', 'Ghusl'],
  },
  {
    id: 7,
    question: 'Surah Al-Ikhlas is about the unity of whom?',
    answer: 'Allah (God)',
    options: ['Allah (God)', 'The Prophet Muhammad', 'The Angels'],
  },
];

// Unique key for score storage in localStorage
const SCORE_STORAGE_KEY = 'studentQuizScore_';

export default function Quiz({ studentId, suraId, suraName }) {
  const [score, setScore] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [quizResult, setQuizResult] = useState('');

  // Function to calculate the current medal
  const getMedal = (currentScore) => {
    if (currentScore >= MEDAL_THRESHOLDS.GOLD)
      return { name: 'Gold', color: 'text-yellow-500' };
    if (currentScore >= MEDAL_THRESHOLDS.SILVER)
      return { name: 'Silver', color: 'text-gray-400' };
    if (currentScore >= MEDAL_THRESHOLDS.BRONZE)
      return { name: 'Bronze', color: 'text-yellow-800' };
    return { name: 'None', color: 'text-gray-500' };
  };

  const currentMedal = getMedal(score);

  // Effect: Load Score
  useEffect(() => {
    const key = `${SCORE_STORAGE_KEY}${studentId}`;
    const savedScore = localStorage.getItem(key);
    if (savedScore) {
      setScore(parseInt(savedScore, 10));
    }
  }, [studentId]);

  // Function to load a new question
  const loadNewQuestion = () => {
    const randomIndex = Math.floor(Math.random() * QUIZ_QUESTIONS.length);
    setCurrentQuestion(QUIZ_QUESTIONS[randomIndex]);
    setQuizResult('');
  };

  // Effect: Load new question when the Sura changes
  useEffect(() => {
    loadNewQuestion();
  }, [suraId]);

  // Function to update and save the score
  const updateScore = (points) => {
    const newScore = score + points;
    setScore(newScore);
    const key = `${SCORE_STORAGE_KEY}${studentId}`;
    localStorage.setItem(key, newScore.toString());
  };

  // Answer Submission Handler
  const handleSubmitAnswer = (e) => {
    e.preventDefault();
    const userAnswer = e.target.answer.value.trim();
    e.target.answer.value = ''; // Clear the input

    if (!currentQuestion) return;

    // Simple check: check if the user's answer includes the first few letters of the correct answer
    const isCorrect = userAnswer
      .toLowerCase()
      .includes(currentQuestion.answer.toLowerCase().substring(0, 4));

    if (isCorrect) {
      setQuizResult('Correct! 🎉');
      updateScore(100);
    } else {
      setQuizResult(
        `Incorrect. The correct answer was: ${currentQuestion.answer}`,
      );
    }

    // Load the next question after a brief delay
    setTimeout(loadNewQuestion, 2000);
  };

  return (
    <div className='space-y-4'>
      {/* Score and Medal Display */}
      <div className='flex justify-between items-center p-3 bg-gray-50 border rounded-lg shadow-inner'>
        <div className='text-center'>
          <HiFire className='w-6 h-6 text-red-500 mx-auto' />
          <span className='text-xl font-bold text-gray-800'>
            {score} Points
          </span>
        </div>
        <div className='text-center'>
          <HiShieldCheck className={`w-8 h-8 mx-auto ${currentMedal.color}`} />
          <span className='font-semibold text-gray-700'>
            {currentMedal.name} Medal
          </span>
        </div>
      </div>

      {/* Quiz Section */}
      <div className='bg-white p-4 border border-indigo-100 rounded-lg shadow'>
        <h4 className='text-md font-semibold text-indigo-600 mb-3 flex items-center'>
          <HiSparkles className='w-5 h-5 mr-1' />
          Knowledge Test
        </h4>

        {currentQuestion ? (
          <form onSubmit={handleSubmitAnswer} className='space-y-3'>
            <p className='text-gray-800 font-medium'>
              {currentQuestion.question}
            </p>
            <input
              type='text'
              name='answer'
              placeholder='Type your answer here...'
              className='w-full p-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500'
              required
            />
            <button
              type='submit'
              className='w-full py-2 bg-indigo-600 text-white font-medium rounded-md shadow hover:bg-indigo-700 transition'
            >
              Answer
            </button>
          </form>
        ) : (
          <p className='text-gray-500'>Loading question...</p>
        )}

        {quizResult && (
          <p
            className={`mt-3 text-center font-bold ${quizResult.includes('Correct') ? 'text-green-600' : 'text-red-500'}`}
          >
            {quizResult}
          </p>
        )}
      </div>

      {/* Medal Threshold Info */}
      <div className='text-xs text-gray-500 pt-2 border-t'>
        **Goals:** Bronze: {MEDAL_THRESHOLDS.BRONZE}p, Silver:{' '}
        {MEDAL_THRESHOLDS.SILVER}p, Gold: {MEDAL_THRESHOLDS.GOLD}p.
      </div>
    </div>
  );
}
